import { useState } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { signupTenant } from '../services/api';

// Public self-service tenant signup. Lives on the base host; on success the new
// tenant's subdomain is reachable immediately (wildcard DNS + cert).
export default function SignupPage() {
  const [slug, setSlug] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState(''); // honeypot — must stay empty
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // The signup page is reached either on the base host itself (e.g.
  // staging1.hallochef.net) or via an entry subdomain under the base domain
  // (www./app./signup.hallochef.dev). Strip a leading entry label so the
  // preview shows the real tenant base (<slug>.hallochef.dev, not
  // <slug>.www.hallochef.dev). The backend always uses TENANT_BASE_DOMAIN
  // regardless — this only fixes the displayed hint.
  const hostParts = window.location.host.split('.');
  const baseHost = hostParts.length > 2 && ['www', 'app', 'signup'].includes(hostParts[0])
    ? hostParts.slice(1).join('.')
    : window.location.host;
  const normalizedSlug = slug.trim().toLowerCase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await signupTenant({
        slug: normalizedSlug,
        business_name: businessName,
        email,
        password,
        company,
      });
      setSentTo(res.data?.email || email);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Signup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sentTo) {
    return (
      <Container className="py-5" style={{ maxWidth: 520 }}>
        <Alert variant="success">
          <Alert.Heading>Provjerite email 📧</Alert.Heading>
          <p className="mb-0">
            Poslali smo poveznicu za potvrdu na <strong>{sentTo}</strong>.
            Kliknite je da dovršimo izradu vašeg restorana. (Link vrijedi 24 sata.)
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5" style={{ maxWidth: 520 }}>
      <Card>
        <Card.Body>
          <h3 className="mb-1">Otvori svoj HalloChef</h3>
          <p className="text-muted">Kreirajte račun i odmah počnite primati narudžbe.</p>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            {/* Honeypot: off-screen + hidden from a11y tree; bots fill it, users don't. */}
            <input
              type="text"
              name="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
            <Form.Group className="mb-3">
              <Form.Label>Adresa (poddomena)</Form.Label>
              <InputGroup>
                <Form.Control
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="moj-restoran"
                  autoFocus
                  required
                />
                <InputGroup.Text>.{baseHost}</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Mala slova, brojevi i crtice; počinje slovom.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Naziv restorana</Form.Label>
              <Form.Control value={businessName} onChange={(e) => setBusinessName(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email administratora</Form.Label>
              <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lozinka</Form.Label>
              <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
              <Form.Text className="text-muted">Najmanje 8 znakova.</Form.Text>
            </Form.Group>
            <Button type="submit" variant="success" className="w-100" disabled={submitting}>
              {submitting ? <><Spinner animation="border" size="sm" /> Kreiram…</> : 'Kreiraj restoran'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
